// The signed outbox. A capture writes here and returns at once; flush() drains it in order:
// blobs first, then the event. Nothing is ever deleted: rejected events stay for staff to see.
import { signCanonical, sha256Hex, validatePreImage, type PreImage } from "@vavr/core";
import type { OutboxBlob, OutboxEntry, VavrDb } from "./db.ts";
import { ApiError, type DeviceApi } from "./api.ts";

export interface CaptureBlob { bytes: Uint8Array; type: string }

// Offline phase 2 (PMU-271): every record saved on the device is also offered to the door-as-hub
// link, so the other devices on the school's Wi-Fi see it with the internet down.
const enqueued = new Set<(e: OutboxEntry) => void>();
export function onEnqueue(f: (e: OutboxEntry) => void) { enqueued.add(f); return () => { enqueued.delete(f); }; }

/**
 * `onServer`: hashes the site already holds, so the event may reference them without the bytes
 * (a staff phone's release reuses the photo and signature the kiosk uploaded with its request).
 */
export async function enqueue(db: VavrDb, privateKey: CryptoKey, pre: PreImage, blobs: CaptureBlob[], opts: { onServer?: string[] } = {}): Promise<OutboxEntry> {
  const problems = validatePreImage(pre);
  if (problems.length) throw new Error(`event invalid: ${problems.join("; ")}`);
  const withHashes: OutboxBlob[] = [];
  for (const b of blobs) withHashes.push({ sha256: await sha256Hex(b.bytes), type: b.type, bytes: b.bytes, uploaded: false });
  for (const h of [pre.photo_sha256, pre.stroke_sha256]) {
    if (h && !withHashes.some((b) => b.sha256 === h) && !opts.onServer?.includes(h)) throw new Error(`blob ${h} referenced but not supplied`);
  }
  const entry: OutboxEntry = {
    id: pre.id, created_at: Date.now(), pre, device_sig: await signCanonical(privateKey, pre), blobs: withHashes,
    state: "pending", attempts: 0, last_error: null, seq: null, event_hash: null, server_flags: [],
  };
  await db.outbox.put(entry);
  for (const f of enqueued) { try { f(entry); } catch { /* a listener never blocks a capture */ } }
  return entry;
}

export async function pendingCount(db: VavrDb) {
  return db.outbox.where("state").equals("pending").count();
}

export interface FlushResult { sent: number; rejected: number; remaining: number; offline: boolean; /** All refused records kept on this device. */ refused?: number }

let flushing: Promise<FlushResult> | null = null;

/** Sends pending events oldest first. Concurrent calls share one run. */
export function flush(db: VavrDb, api: DeviceApi): Promise<FlushResult> {
  flushing ??= doFlush(db, api).finally(() => { flushing = null; });
  return flushing;
}

async function doFlush(db: VavrDb, api: DeviceApi): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, rejected: 0, remaining: 0, offline: false };
  const pending = await db.outbox.where("state").equals("pending").sortBy("created_at");
  for (const e of pending) {
    try {
      for (const b of e.blobs) {
        if (b.uploaded) continue;
        const res = await api.request("PUT", `/api/v1/device/blobs/${b.sha256}`, { bytes: b.bytes, type: b.type });
        if (!res.ok) { const d = await res.json().catch(() => ({})) as any; throw new ApiError(res.status, d.error ?? "blob", d.message); }
        b.uploaded = true;
        await db.outbox.update(e.id, { blobs: e.blobs });
      }
      const r = await api.json("POST", "/api/v1/device/events", { json: { pre: e.pre, device_sig: e.device_sig, device_now: Date.now() } });
      await db.outbox.update(e.id, { state: "sent", seq: r.seq, event_hash: r.event_hash, server_flags: r.server_flags, attempts: e.attempts + 1, last_error: null });
      result.sent++;
    } catch (err) {
      if (err instanceof ApiError) {
        // 409 "blob not uploaded" or rate limits are transient; anything else in 4xx is final.
        const transient = err.status >= 500 || err.status === 409 || err.status === 429 || err.code === "device_clock_skew" || err.code === "device_pending";
        await db.outbox.update(e.id, { attempts: e.attempts + 1, last_error: `${err.status} ${err.message}`, state: transient ? "pending" : "rejected" });
        if (!transient) { result.rejected++; continue; }
        if (err.code === "device_pending" || err.code === "device_clock_skew") break;
        continue;
      }
      // Network failure: stop; the next trigger retries from here, in order.
      await db.outbox.update(e.id, { attempts: e.attempts + 1, last_error: String((err as Error).message ?? err) });
      result.offline = true;
      break;
    }
  }
  result.remaining = await pendingCount(db);
  result.refused = await db.outbox.where("state").equals("rejected").count();
  return result;
}

/** Drains on `online`, on returning to the foreground, and every 30 seconds (plan §4 D6). */
export function startFlushLoop(db: VavrDb, api: DeviceApi, onResult: (r: FlushResult) => void, target: { addEventListener?: (t: string, f: () => void) => void } = globalThis as never) {
  const run = () => { flush(db, api).then(onResult, () => undefined); };
  target.addEventListener?.("online", run);
  target.addEventListener?.("visibilitychange", run);
  const timer = setInterval(run, 30_000);
  run();
  return () => clearInterval(timer);
}

/**
 * Records refused because the request arrived altered in transit (not because of their content)
 * go back to pending once: they were signed correctly and deserve another send.
 */
export async function retryTransportRefusals(db: VavrDb): Promise<number> {
  const transport = /^(401 bad_device_signature|422 hash_mismatch|422 The upload does not match its hash\.|401 The upload)/;
  const all = await db.outbox.where("state").equals("rejected").toArray();
  let n = 0;
  for (const e of all) {
    if (e.last_error && transport.test(e.last_error) && !(e as OutboxEntry & { retried?: boolean }).retried) {
      await db.outbox.update(e.id, { state: "pending", ...( { retried: true } as object) });
      n++;
    }
  }
  return n;
}
